import React, { useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, Crosshair, Play, Target, Users, Zap } from 'lucide-react';
import { GameConfig, GameMode, InputMethod, OutMode, Player } from '../types/darts';
import { useAppTheme } from '../hooks/useAppTheme';
import { useT } from '../hooks/useT';
import { MAX_NAME_LENGTH, cleanName, ensureProfiles, findDuplicateName, getProfiles } from '../utils/players';
import type { TranslationKey } from '../utils/i18n';

interface SetupScreenProps {
  onStartGame: (config: GameConfig, players: Player[]) => void;
}

const PLAYER_COLORS = [
  'from-emerald-500 to-teal-600',
  'from-red-600 via-amber-500 to-emerald-600',
  'from-blue-500 to-indigo-600',
  'from-amber-500 to-orange-600'
];

const LOCAL_STORAGE_KEY_NAMES = 'dartmaster_saved_player_names';
const DEFAULT_NAME = /^(speler|player) \d+$/i;

const GAME_MODES: { id: GameMode; label?: string; labelKey?: TranslationKey; descKey: TranslationKey }[] = [
  { id: '901', label: '901', descKey: 'mode901Desc' },
  { id: '501', label: '501', descKey: 'mode501Desc' },
  { id: '301', label: '301', descKey: 'mode301Desc' },
  { id: 'countup', labelKey: 'modeCountUp', descKey: 'modeCountUpDesc' },
  { id: 'cricket', labelKey: 'modeCricket', descKey: 'modeCricketDesc' }
];

const OUT_MODES: { id: OutMode; key: TranslationKey }[] = [
  { id: 'double', key: 'outDouble' },
  { id: 'single', key: 'outSingle' },
  { id: 'master', key: 'outMaster' }
];

const LEG_OPTIONS = [1, 2, 3, 5, 7];
const COUNT_UP_TARGETS = [300, 500, 1000];

function readSavedNames(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY_NAMES) || '[]');
    if (Array.isArray(parsed) && parsed.every(n => typeof n === 'string')) return parsed;
  } catch {
    // kapotte opslag: begin leeg
  }
  return [];
}

function Section({ icon, label, children }: { icon?: ReactNode; label: string; children: ReactNode }) {
  const id = useId();
  return (
    <div role="group" aria-labelledby={id} className="min-w-0 space-y-3 rounded-2xl bg-black/5 p-4 dark:bg-white/5">
      <h2 id={id} className="flex items-center gap-2 text-sm font-bold">
        {icon}
        {label}
      </h2>
      {children}
    </div>
  );
}

export function SetupScreen({ onStartGame }: SetupScreenProps) {
  const { t, tf } = useT();
  const { theme } = useAppTheme();
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState<string[]>(readSavedNames);
  const [gameMode, setGameMode] = useState<GameMode>('501');
  const [countUpTarget, setCountUpTarget] = useState(500);
  const [outMode, setOutMode] = useState<OutMode>('double');
  const [firstToLegs, setFirstToLegs] = useState(1);
  const [inputMethod, setInputMethod] = useState<InputMethod>('per-dart');
  const [nameError, setNameError] = useState<string | null>(null);
  // Bekende spelers als suggestie bij het typen van een naam.
  const [knownNames] = useState<string[]>(() => getProfiles().map(p => p.name));

  const selected = `${theme.keypadPrimaryBg} ${theme.keypadPrimaryText}`;
  const choice = (active: boolean) =>
    `min-h-11 rounded-2xl px-3 text-sm font-semibold whitespace-nowrap transition-all duration-150 active:scale-95 ${
      active ? selected : 'bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-800/80 dark:text-slate-100 dark:hover:bg-slate-700'
    }`;

  const updateNames = (updated: string[]) => {
    setPlayerNames(updated);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_NAMES, JSON.stringify(updated));
    } catch {
      // opslaan van namen is een gemak, geen vereiste
    }
  };

  const handleNameChange = (index: number, value: string) => {
    const updated = [...playerNames];
    while (updated.length <= index) updated.push('');
    updated[index] = value;
    setNameError(null);
    updateNames(updated);
  };

  const movePlayer = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= playerCount) return;
    const updated = [...playerNames];
    while (updated.length < playerCount) updated.push('');
    [updated[index], updated[target]] = [updated[target], updated[index]];
    updateNames(updated);
  };

  const nameFor = (i: number) => {
    const name = cleanName(playerNames[i] || '');
    return name && !DEFAULT_NAME.test(name) ? name : tf('playerDefault', { n: i + 1 });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const names = Array.from({ length: playerCount }, (_, i) => nameFor(i));
    const duplicate = findDuplicateName(names);
    if (duplicate) {
      setNameError(tf('duplicateName', { name: duplicate }));
      return;
    }

    // Elke naam hoort bij een vast spelersprofiel; zo blijven statistieken per speler bij elkaar.
    const profiles = ensureProfiles(names);
    const players: Player[] = profiles.map((profile, i) => ({
      id: profile.id,
      name: profile.name,
      color: PLAYER_COLORS[i % PLAYER_COLORS.length]
    }));

    const startingScore =
      gameMode === 'countup' ? Math.max(1, countUpTarget) : gameMode === 'cricket' ? 0 : Number(gameMode);

    onStartGame({ gameMode, startingScore, outMode, firstToLegs, firstToSets: 1, inputMethod }, players);
  };

  return (
    <div className="mx-auto w-full max-w-xl py-2">
      <div className="space-y-4 rounded-3xl bg-white/80 p-4 shadow-sm backdrop-blur-md dark:bg-slate-900/80 sm:p-6">
        <div className="text-center">
          <h1 className="text-xl font-bold">{t('setupTitle')}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">{t('setupSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Section icon={<Users size={18} className={theme.textAccent} aria-hidden />} label={t('playersLabel')}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">{t('playerCountLabel')}</span>
              <div className="flex gap-1" role="group" aria-label={t('playerCountLabel')}>
                {[1, 2, 3, 4].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPlayerCount(num)}
                    aria-pressed={playerCount === num}
                    className={`w-11 font-mono ${choice(playerCount === num)}`}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {Array.from({ length: playerCount }, (_, i) => (
                <div key={i} className="flex items-center gap-1 rounded-2xl bg-white px-3 dark:bg-slate-800/80">
                  <span className="w-5 shrink-0 font-mono text-sm font-bold text-slate-600 dark:text-slate-400" aria-hidden>
                    {i + 1}
                  </span>
                  <input
                    type="text"
                    value={playerNames[i] && !DEFAULT_NAME.test(playerNames[i]) ? playerNames[i] : ''}
                    onChange={e => handleNameChange(i, e.target.value)}
                    placeholder={tf('playerDefault', { n: i + 1 })}
                    aria-label={tf('playerNameLabel', { n: i + 1 })}
                    list="known-players"
                    autoComplete="off"
                    maxLength={MAX_NAME_LENGTH}
                    className="min-h-11 w-full min-w-0 border-none bg-transparent text-base outline-none placeholder:text-slate-500 dark:placeholder:text-slate-400"
                  />
                  {playerCount > 1 && (
                    <>
                      <button
                        type="button"
                        disabled={i === 0}
                        onClick={() => movePlayer(i, -1)}
                        aria-label={tf('moveUp', { n: i + 1 })}
                        className="grid h-11 w-9 shrink-0 place-items-center rounded-xl text-slate-600 transition hover:bg-black/5 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-white/10"
                      >
                        <ChevronUp size={18} aria-hidden />
                      </button>
                      <button
                        type="button"
                        disabled={i === playerCount - 1}
                        onClick={() => movePlayer(i, 1)}
                        aria-label={tf('moveDown', { n: i + 1 })}
                        className="grid h-11 w-9 shrink-0 place-items-center rounded-xl text-slate-600 transition hover:bg-black/5 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-white/10"
                      >
                        <ChevronDown size={18} aria-hidden />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <datalist id="known-players">
              {knownNames.map(name => (
                <option key={name} value={name} />
              ))}
            </datalist>
            {nameError && (
              <p role="alert" className="rounded-2xl bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-800 dark:text-red-300">
                {nameError}
              </p>
            )}
          </Section>

          <Section icon={<Target size={18} className={theme.textAccent} aria-hidden />} label={t('gameTypeLabel')}>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {GAME_MODES.map(mode => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setGameMode(mode.id)}
                  aria-pressed={gameMode === mode.id}
                  className={`flex flex-col items-center justify-center py-2 ${choice(gameMode === mode.id)}`}
                >
                  <span className="font-bold">{mode.label ?? t(mode.labelKey!)}</span>
                  <span className="text-xs font-medium">{t(mode.descKey)}</span>
                </button>
              ))}
            </div>

            {gameMode === 'countup' && (
              <div className="space-y-2 pt-1">
                <p className="text-sm text-slate-600 dark:text-slate-400">{t('countUpTargetLabel')}</p>
                <div className="grid grid-cols-4 gap-2">
                  {COUNT_UP_TARGETS.map(tgt => (
                    <button
                      key={tgt}
                      type="button"
                      onClick={() => setCountUpTarget(tgt)}
                      aria-pressed={countUpTarget === tgt}
                      className={`font-mono ${choice(countUpTarget === tgt)}`}
                    >
                      {tf('pointsUnit', { n: tgt })}
                    </button>
                  ))}
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={countUpTarget || ''}
                    onFocus={e => e.target.select()}
                    onChange={e => setCountUpTarget(parseInt(e.target.value, 10) || 0)}
                    aria-label={t('customTarget')}
                    className="min-h-11 w-full rounded-2xl border-none bg-white px-2 text-center font-mono text-base outline-none dark:bg-slate-800/80"
                  />
                </div>
              </div>
            )}
          </Section>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {gameMode !== 'countup' && gameMode !== 'cricket' && (
                <Section label={t('outModeLabel')}>
                  <div className="flex flex-wrap gap-2">
                    {OUT_MODES.map(mode => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setOutMode(mode.id)}
                        aria-pressed={outMode === mode.id}
                        className={choice(outMode === mode.id)}
                      >
                        {t(mode.key)}
                      </button>
                    ))}
                  </div>
                </Section>
              )}
              <Section label={t('matchLengthLabel')}>
                <select
                  value={firstToLegs}
                  onChange={e => setFirstToLegs(Number(e.target.value))}
                  aria-label={t('matchLengthLabel')}
                  className="min-h-11 w-full rounded-2xl border-none bg-white px-3 text-base outline-none dark:bg-slate-800/80"
                >
                  {LEG_OPTIONS.map(n => (
                    <option key={n} value={n}>
                      {n === 1 ? t('firstToOne') : tf('firstToMany', { n })}
                    </option>
                  ))}
                </select>
              </Section>
          </div>

          {gameMode !== 'cricket' && (
            <Section label={t('inputMethodLabel')}>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { id: 'per-dart', Icon: Crosshair, key: 'inputPerDart', descKey: 'inputPerDartDesc' },
                    { id: 'turn-total', Icon: Zap, key: 'inputTurnTotal', descKey: 'inputTurnTotalDesc' }
                  ] as const
                ).map(({ id, Icon, key, descKey }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setInputMethod(id)}
                    aria-pressed={inputMethod === id}
                    className={`flex items-center gap-2 py-2 text-left ${choice(inputMethod === id)}`}
                  >
                    <Icon size={18} aria-hidden />
                    <span className="min-w-0">
                      <span className="block">{t(key)}</span>
                      <span className="block text-xs font-medium">{t(descKey)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </Section>
          )}

          <button
            type="submit"
            className={`flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl text-base font-bold whitespace-nowrap shadow-sm transition-all duration-150 active:scale-95 ${selected}`}
          >
            <Play size={18} className="fill-current" aria-hidden />
            {t('startGame')}
          </button>
        </form>
      </div>
    </div>
  );
}
